import React, { useEffect, useState, useRef } from 'react'
import { Wifi } from '@mui/icons-material'
import { Button } from '../../index'
import * as S from './styles'

interface VideoPermissionsProps {
  onConfirm: () => void
  onClose: () => void
  setShowPermissions: (permissions: { camera: boolean; microphone: boolean }) => void
  showPermissions: { camera: boolean; microphone: boolean }
}

const VideoPermissions: React.FC<VideoPermissionsProps> = ({
  onConfirm,
  onClose,
  setShowPermissions,
}) => {
  const [cameraAccess, setCameraAccess] = useState<string | null>(null)
  const [microphoneAccess, setMicrophoneAccess] = useState<string | null>(null)
  const [isRequesting, setIsRequesting] = useState(false)
  const [showInstructions, setShowInstructions] = useState(false)
  const [networkQuality, setNetworkQuality] = useState<{
    effectiveType: string
    downlink: number
    rtt: number
    saveData: boolean
    status: 'excellent' | 'good' | 'poor' | 'critical'
  } | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)

  const checkSingleDevicePermission = async (
    device: 'camera' | 'microphone'
  ): Promise<'granted' | 'denied' | 'prompt'> => {
    try {
      const constraints = device === 'camera' ? { video: true } : { audio: true }
      const stream = await navigator.mediaDevices.getUserMedia(constraints)
      stream.getTracks().forEach((track) => track.stop())
      return 'granted'
    } catch (error: any) {
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        return 'denied'
      }
      return 'prompt'
    }
  }

  const checkPermissionStatus = async () => {
    try {
      if (navigator.permissions && navigator.permissions.query) {
        const cameraStatus = await navigator.permissions.query({
          name: 'camera' as PermissionName,
        })
        const microphoneStatus = await navigator.permissions.query({
          name: 'microphone' as PermissionName,
        })

        setCameraAccess(
          cameraStatus.state === 'granted'
            ? 'granted'
            : cameraStatus.state === 'denied'
              ? 'denied'
              : 'prompt'
        )

        setMicrophoneAccess(
          microphoneStatus.state === 'granted'
            ? 'granted'
            : microphoneStatus.state === 'denied'
              ? 'denied'
              : 'prompt'
        )

        setShowPermissions({
          camera: cameraStatus.state === 'granted',
          microphone: microphoneStatus.state === 'granted',
        })

        cameraStatus.onchange = () => {
          const newState =
            cameraStatus.state === 'granted'
              ? 'granted'
              : cameraStatus.state === 'denied'
                ? 'denied'
                : 'prompt'
          setCameraAccess(newState)
          setShowPermissions({
            camera: cameraStatus.state === 'granted',
            microphone: microphoneStatus.state === 'granted',
          })
        }
        microphoneStatus.onchange = () => {
          const newState =
            microphoneStatus.state === 'granted'
              ? 'granted'
              : microphoneStatus.state === 'denied'
                ? 'denied'
                : 'prompt'
          setMicrophoneAccess(newState)
          setShowPermissions({
            camera: cameraStatus.state === 'granted',
            microphone: microphoneStatus.state === 'granted',
          })
        }
      } else {
        setCameraAccess('prompt')
        setMicrophoneAccess('prompt')
      }
    } catch (error: any) {
      console.error('Erro ao verificar permissões:', error)
      setCameraAccess('prompt')
      setMicrophoneAccess('prompt')
    }
  }

  const requestPermissions = async () => {
    setIsRequesting(true)
    try {
      const cameraResult = await checkSingleDevicePermission('camera')
      const microphoneResult = await checkSingleDevicePermission('microphone')

      setCameraAccess(cameraResult)
      setMicrophoneAccess(microphoneResult)

      setShowPermissions({
        camera: cameraResult === 'granted',
        microphone: microphoneResult === 'granted',
      })

      if (cameraResult === 'denied' || microphoneResult === 'denied') {
        setShowInstructions(true)
      } else {
        setShowInstructions(false)
      }
    } catch (error) {
      console.error('Erro ao verificar permissões:', error)
      const cameraResult = await checkSingleDevicePermission('camera')
      const microphoneResult = await checkSingleDevicePermission('microphone')
      setCameraAccess(cameraResult)
      setMicrophoneAccess(microphoneResult)
      setShowPermissions({
        camera: cameraResult === 'granted',
        microphone: microphoneResult === 'granted',
      })
    } finally {
      setIsRequesting(false)
    }
  }

  const checkNetworkQuality = () => {
    // @ts-ignore - navigator.connection pode não estar disponível em todos os navegadores
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection

    if (connection) {
      const effectiveType = connection.effectiveType || 'unknown'
      const downlink = connection.downlink || 0
      const rtt = connection.rtt || 0
      const saveData = connection.saveData || false

      let status: 'excellent' | 'good' | 'poor' | 'critical' = 'good'

      if (effectiveType === '4g' && downlink >= 10) {
        status = 'excellent'
      } else if (effectiveType === '4g' && downlink >= 1.5) {
        status = 'good'
      } else if (effectiveType === '3g' || (effectiveType === '4g' && downlink < 1.5)) {
        status = 'poor'
      } else if (effectiveType === '2g' || effectiveType === 'slow-2g') {
        status = 'critical'
      } else if (rtt > 1000) {
        status = 'poor'
      } else if (rtt > 2000) {
        status = 'critical'
      }

      setNetworkQuality({
        effectiveType,
        downlink,
        rtt,
        saveData,
        status,
      })

      connection.addEventListener('change', () => {
        const updatedEffectiveType = connection.effectiveType || 'unknown'
        const updatedDownlink = connection.downlink || 0
        const updatedRtt = connection.rtt || 0

        let updatedStatus: 'excellent' | 'good' | 'poor' | 'critical' = 'good'

        if (updatedEffectiveType === '4g' && updatedDownlink >= 10) {
          updatedStatus = 'excellent'
        } else if (updatedEffectiveType === '4g' && updatedDownlink >= 1.5) {
          updatedStatus = 'good'
        } else if (updatedEffectiveType === '3g' || (updatedEffectiveType === '4g' && updatedDownlink < 1.5)) {
          updatedStatus = 'poor'
        } else if (updatedEffectiveType === '2g' || updatedEffectiveType === 'slow-2g') {
          updatedStatus = 'critical'
        } else if (updatedRtt > 1000) {
          updatedStatus = 'poor'
        } else if (updatedRtt > 2000) {
          updatedStatus = 'critical'
        }

        setNetworkQuality({
          effectiveType: updatedEffectiveType,
          downlink: updatedDownlink,
          rtt: updatedRtt,
          saveData: connection.saveData || false,
          status: updatedStatus,
        })
      })
    } else {
      const isOnline = navigator.onLine
      setNetworkQuality({
        effectiveType: isOnline ? 'unknown' : 'offline',
        downlink: 0,
        rtt: 0,
        saveData: false,
        status: isOnline ? 'good' : 'critical',
      })
    }
  }

  // Inicia a câmera quando a permissão for concedida
  const startCamera = async () => {
    if (cameraAccess !== 'granted') return

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true })
      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play().catch((error) => {
            console.error('Erro ao iniciar o vídeo:', error)
          })
        }
      }
    } catch (error: any) {
      console.error('Erro ao acessar a câmera:', error)
    }
  }

  // Para a câmera
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        if (track.readyState !== 'ended') {
          track.stop()
        }
      })
      streamRef.current = null
    }

    if (videoRef.current) {
      if (videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject as MediaStream
        stream.getTracks().forEach((track) => {
          if (track.readyState !== 'ended') {
            track.stop()
          }
        })
        videoRef.current.srcObject = null
      }
      videoRef.current.pause()
    }
  }

  useEffect(() => {
    checkPermissionStatus()
    checkNetworkQuality()
  }, [])

  // Inicia a câmera quando a permissão for concedida
  useEffect(() => {
    if (cameraAccess === 'granted') {
      startCamera()
    } else {
      stopCamera()
    }

    return () => {
      stopCamera()
    }
  }, [cameraAccess])

  const hasDeniedPermissions =
    ['denied', 'prompt'].includes(cameraAccess ?? '') ||
    ['denied', 'prompt'].includes(microphoneAccess ?? '')

  const getPermissionButtonText = () => {
    const cameraMissing = cameraAccess !== 'granted'
    const microphoneMissing = microphoneAccess !== 'granted'

    if (cameraMissing && microphoneMissing) {
      return 'Solicitar permissões'
    } else if (cameraMissing) {
      return 'Permitir acesso à câmera'
    } else if (microphoneMissing) {
      return 'Permitir acesso ao microfone'
    }
    return 'Solicitar permissões'
  }

  return (
    <>
      <S.Overlay onClick={onClose} />
      <S.ModalContainer onClick={(e) => e.stopPropagation()}>
        <S.ModalHeader>
          <S.ModalTitle>Permissões Necessárias</S.ModalTitle>
          <S.CloseButton onClick={onClose}>×</S.CloseButton>
        </S.ModalHeader>
        <S.ModalContent>
          {showInstructions && hasDeniedPermissions && (
            <S.InstructionsBox>
              <S.InstructionsIcon>⚠</S.InstructionsIcon>
              <S.InstructionsContent>
                <S.InstructionsTitle>Permissões negadas</S.InstructionsTitle>
                <S.InstructionsText>
                  Por favor, permita o acesso à câmera e microfone nas configurações
                  do navegador.
                  {typeof window !== 'undefined' && 'chrome' in window && (
                    <span> Clique no ícone de cadeado na barra de endereço.</span>
                  )}
                </S.InstructionsText>
              </S.InstructionsContent>
            </S.InstructionsBox>
          )}

          <S.PermissionsCard>
            <S.PermissionsTitle>
              Status das Permissões
            </S.PermissionsTitle>
            <S.PermissionsList>
              <S.PermissionItem>
                <S.PermissionInfo>
                  <S.PermissionIcon status={cameraAccess}>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                      <circle cx="12" cy="13" r="4" />
                    </svg>
                  </S.PermissionIcon>
                  <S.PermissionText>
                    <S.PermissionLabel>Câmera</S.PermissionLabel>
                    <S.StatusText status={cameraAccess}>
                      {cameraAccess === 'granted'
                        ? 'Permitida'
                        : cameraAccess === 'prompt'
                          ? 'Aguardando permissão'
                          : 'Negada'}
                    </S.StatusText>
                  </S.PermissionText>
                </S.PermissionInfo>
              </S.PermissionItem>

              <S.PermissionItem>
                <S.PermissionInfo>
                  <S.PermissionIcon status={microphoneAccess}>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                      <line x1="12" y1="19" x2="12" y2="23" />
                      <line x1="8" y1="23" x2="16" y2="23" />
                    </svg>
                  </S.PermissionIcon>
                  <S.PermissionText>
                    <S.PermissionLabel>Microfone</S.PermissionLabel>
                    <S.StatusText status={microphoneAccess}>
                      {microphoneAccess === 'granted'
                        ? 'Permitido'
                        : microphoneAccess === 'prompt'
                          ? 'Aguardando permissão'
                          : 'Negado'}
                    </S.StatusText>
                  </S.PermissionText>
                </S.PermissionInfo>
              </S.PermissionItem>
            </S.PermissionsList>
          </S.PermissionsCard>

          {networkQuality && (
            <S.CameraNetworkContainer>
              <S.CameraPreview>
                {cameraAccess === 'granted' ? (
                  <S.CameraVideo ref={videoRef} autoPlay playsInline muted />
                ) : (
                  <S.CameraPlaceholder>
                    <S.CameraPlaceholderIcon>
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                        <circle cx="12" cy="13" r="4" />
                      </svg>
                    </S.CameraPlaceholderIcon>
                    <S.CameraPlaceholderText>
                      {cameraAccess === 'denied'
                        ? 'Câmera bloqueada'
                        : 'Aguardando permissão'}
                    </S.CameraPlaceholderText>
                  </S.CameraPlaceholder>
                )}
              </S.CameraPreview>
              <S.NetworkCard>
                <S.NetworkTitle>
                  Qualidade da Conexão
                </S.NetworkTitle>
                <S.NetworkStatus>
                  <S.NetworkInfo>
                    <S.NetworkIcon status={networkQuality.status}>
                      <Wifi />
                    </S.NetworkIcon>
                    <S.NetworkText>
                      <S.NetworkLabel>Status da Internet</S.NetworkLabel>
                      <S.NetworkStatusText status={networkQuality.status}>
                        {networkQuality.status === 'excellent'
                          ? 'Excelente'
                          : networkQuality.status === 'good'
                            ? 'Boa'
                            : networkQuality.status === 'poor'
                              ? 'Ruim'
                              : networkQuality.status === 'critical'
                                ? 'Crítica'
                                : 'Offline'}
                      </S.NetworkStatusText>
                    </S.NetworkText>
                  </S.NetworkInfo>
                  {networkQuality.downlink > 0 && (
                    <S.NetworkDetails>
                      <S.NetworkDetailItem>
                        <span>Velocidade:</span>{' '}
                        <strong>{networkQuality.downlink.toFixed(1)} Mbps</strong>
                      </S.NetworkDetailItem>
                      {networkQuality.rtt > 0 && (
                        <S.NetworkDetailItem>
                          <span>Latência:</span> <strong>{networkQuality.rtt}ms</strong>
                        </S.NetworkDetailItem>
                      )}
                    </S.NetworkDetails>
                  )}
                </S.NetworkStatus>
                
              </S.NetworkCard>
            </S.CameraNetworkContainer>
          )}

          {networkQuality && (networkQuality.status === 'poor' || networkQuality.status === 'critical') && (
            <S.NetworkWarning>
              <S.WarningIcon>⚠️</S.WarningIcon>
              <S.WarningText>
                <strong>Atenção:</strong> Sua conexão pode impactar negativamente a
                qualidade da videochamada. Recomendamos usar uma conexão mais estável
                ou reduzir o uso de outros aplicativos que consomem internet.
              </S.WarningText>
            </S.NetworkWarning>
          )} 

          <S.TipsSection hasDeniedPermissions={hasDeniedPermissions}>
            <S.TipItem>
              <S.TipIcon>💡</S.TipIcon>
              <S.TipText>
                <strong>Luz importa:</strong> Escolha um local bem iluminado,
                preferencialmente com luz natural.
              </S.TipText>
            </S.TipItem>
            <S.TipItem>
              <S.TipIcon>👤</S.TipIcon>
              <S.TipText>
                <strong>Enquadramento:</strong> Certifique-se de que seu rosto esteja
                centralizado na tela.
              </S.TipText>
            </S.TipItem>
          </S.TipsSection>

          <S.ButtonsContainer>
            {hasDeniedPermissions && (
              <Button
                color="primary"
                type="button"
                size="normal"
                styles={{ width: '100%' }}
                onClick={requestPermissions}
                disabled={isRequesting}
              >
                {getPermissionButtonText() + (isRequesting ? '...' : '')}
              </Button>
            )}

            <Button
              color="success"
              type="button"
              size="normal"
              styles={{ width: '100%' }}
              onClick={onConfirm}
              disabled={hasDeniedPermissions}
            >
              Entrar na vídeochamada
            </Button>
          </S.ButtonsContainer>
        </S.ModalContent>
      </S.ModalContainer>
    </>
  )
}

export default VideoPermissions
